/**
 * Day Calendar View Component
 * Outlook-style daily calendar with hourly time slots
 */

import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  useTheme,
  alpha,
} from '@mui/material'
import {
  format,
  parseISO,
  getHours,
  getMinutes,
  differenceInMinutes,
  isSameDay,
} from 'date-fns'
import { CalendarEventDto } from '@api/types/calendarEvent'

interface DayCalendarViewProps {
  currentDate: Date
  events: CalendarEventDto[]
  onTimeSlotClick?: (date: Date, hour: number) => void
}

const HOUR_HEIGHT = 60 // pixels per hour
const START_HOUR = 0 // Midnight
const END_HOUR = 23 // 11 PM

export function DayCalendarView({
  currentDate,
  events,
  onTimeSlotClick,
}: DayCalendarViewProps) {
  const theme = useTheme()
  const navigate = useNavigate()

  // Generate hour slots
  const hours = useMemo(() => {
    const slots: number[] = []
    for (let h = START_HOUR; h <= END_HOUR; h++) {
      slots.push(h)
    }
    return slots
  }, [])

  // Filter events for the current day
  const dayEvents = useMemo(() => {
    return events.filter((event) => {
      const eventDate = parseISO(event.startDateTime)
      return isSameDay(eventDate, currentDate)
    })
  }, [events, currentDate])

  const getEventPosition = (event: CalendarEventDto) => {
    const startTime = parseISO(event.startDateTime)
    const endTime = parseISO(event.endDateTime)

    const startHour = getHours(startTime)
    const startMinute = getMinutes(startTime)

    // Calculate top position
    const topMinutes = startHour * 60 + startMinute
    const top = topMinutes * (HOUR_HEIGHT / 60)

    // Calculate height based on duration
    const durationMinutes = differenceInMinutes(endTime, startTime)
    const height = Math.max(30, durationMinutes * (HOUR_HEIGHT / 60))

    return { top, height }
  }

  const handleEventClick = (e: React.MouseEvent, eventId: string) => {
    e.stopPropagation()
    navigate(`/events/${eventId}`)
  }

  // Get current time indicator position
  const now = new Date()
  const isToday = isSameDay(currentDate, now)
  const currentTimeTop = isToday
    ? (getHours(now) * 60 + getMinutes(now)) * (HOUR_HEIGHT / 60)
    : null

  return (
    <Paper sx={{ overflow: 'hidden' }}>
      {/* Header */}
      <Box
        sx={{
          py: 2,
          px: 3,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: 'grey.100',
        }}
      >
        <Typography variant="h5">
          {format(currentDate, 'EEEE')}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {format(currentDate, 'MMMM d, yyyy')}
        </Typography>
      </Box>

      {/* Time grid */}
      <Box sx={{ display: 'flex', maxHeight: 700, overflow: 'auto' }}>
        {/* Time labels */}
        <Box sx={{ width: 80, flexShrink: 0 }}>
          {hours.map((hour) => (
            <Box
              key={hour}
              sx={{
                height: HOUR_HEIGHT,
                borderBottom: `1px solid ${theme.palette.divider}`,
                borderRight: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-end',
                pr: 1.5,
                pt: 0.5,
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                {format(new Date().setHours(hour, 0), 'h:mm a')}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Day column */}
        <Box
          sx={{
            position: 'relative',
            flex: 1,
          }}
        >
          {/* Hour grid lines */}
          {hours.map((hour) => (
            <Box
              key={hour}
              onClick={() => onTimeSlotClick?.(currentDate, hour)}
              sx={{
                height: HOUR_HEIGHT,
                borderBottom: `1px solid ${theme.palette.divider}`,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                },
              }}
            />
          ))}

          {/* Current time indicator */}
          {currentTimeTop !== null && (
            <Box
              sx={{
                position: 'absolute',
                top: currentTimeTop,
                left: 0,
                right: 0,
                display: 'flex',
                alignItems: 'center',
                zIndex: 2,
                pointerEvents: 'none',
              }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: 'error.main',
                  ml: -0.75,
                }}
              />
              <Box
                sx={{
                  flex: 1,
                  height: 2,
                  bgcolor: 'error.main',
                }}
              />
            </Box>
          )}

          {/* Events */}
          {dayEvents.map((event) => {
            const { top, height } = getEventPosition(event)
            const startTime = parseISO(event.startDateTime)
            const endTime = parseISO(event.endDateTime)

            return (
              <Box
                key={event.id}
                onClick={(e) => handleEventClick(e, event.id)}
                sx={{
                  position: 'absolute',
                  top,
                  left: 8,
                  right: 8,
                  height,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  borderRadius: 1,
                  p: 1.5,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  boxShadow: 1,
                  '&:hover': {
                    bgcolor: 'primary.dark',
                    zIndex: 1,
                    boxShadow: 3,
                  },
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {event.title}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    opacity: 0.9,
                    display: 'block',
                  }}
                >
                  {format(startTime, 'h:mm a')} - {format(endTime, 'h:mm a')}
                </Typography>
                {height > 60 && event.location?.name && (
                  <Typography
                    variant="caption"
                    sx={{
                      opacity: 0.8,
                      display: 'block',
                      mt: 0.5,
                    }}
                  >
                    📍 {event.location.name}
                  </Typography>
                )}
                {height > 80 && event.quickDescription && (
                  <Typography
                    variant="caption"
                    sx={{
                      opacity: 0.8,
                      display: 'block',
                      mt: 0.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {event.quickDescription}
                  </Typography>
                )}
              </Box>
            )
          })}
        </Box>
      </Box>
    </Paper>
  )
}
